/**
 * Emit hosts for the `ui` analytics group — 27 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { TeamInviteSource } from '@/models/analytics/features/ui';
import { isOnPrem } from '@/utils/buildProfile';
import { SegmentActionName, SegmentObjectName } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';

export function extendTrialModalBackClicked(input: { selectedHours: number }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.ExtendTrialModalBackClicked,
      selectHours: input.selectedHours,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.TrialExtensionModal,
      label: 'back',
      selected_hours: input.selectedHours,
    },
  });
}

export function extendTrialModalExtendClicked(input: { selectedHours: number }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.ExtendTrialModalExtendClicked,
      selectHours: input.selectedHours,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.TrialExtensionModal,
      label: 'extend',
      selected_hours: input.selectedHours,
    },
  });
}

export function trackCreateNoteOrDocumentClicked() {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.ChooseTemplateTypeModal,
      action: ButtonAction.CreateNoteOrDocument,
      label: 'Note or document',
    },
  });
}

export function trackFindReplaceModalCancelled() {
  trackLegacyEvent({
    name: SegmentObjectName.GlobalConfig,
    props: {
      action: SegmentActionName.FindReplaceModalCancelled,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.FindReplaceModal,
    },
  });
}

// global configuration settings
export function trackFindReplaceModalOpened() {
  trackLegacyEvent({
    name: SegmentObjectName.GlobalConfig,
    props: {
      action: SegmentActionName.FindReplaceModalOpened,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.FindReplaceModal,
    },
  });
}

export function trackSendTeamInvitation({
  teamId,
  email,
  teamName,
  teamLink,
  totalEmailCount,
  source,
}: {
  teamId: string;
  email: string;
  teamName: string;
  teamLink?: string;
  totalEmailCount?: number;
  source?: TeamInviteSource;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.SendTeamInvitation,
      teamId: teamId,
      teamName: teamName,
      ...(isOnPrem ? {} : { invitedEmail: email }),
      teamLink: teamLink,
      ...(totalEmailCount !== undefined && { totalEmailCount }),
      ...(source && { source }),
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TeamMemberCreated,
    props: {
      team_id: teamId,
      // PII redaction (OBS-1.C4): email stripped on on-prem — defense-in-depth;
      // the collector blacklist catches "email" exactly, but on-prem must never
      // emit PII regardless. Cloud retains existing behavior.
      ...(isOnPrem ? {} : { email: email }),
      creation_action: 'send_invitation',
      team_name: teamName,
      team_link: teamLink,
      total_email_count: totalEmailCount,
      source: source,
    },
  });
}
